/* Native Gardeners accessibility enhancements, round 4 add-on. Added 2026-10-07.
   Loads after a11y-enhancements.js (version 4), which is left unchanged. Items continue that file's numbering.
   Source of the items: an automated scan report of the site dated 2026-09-14, reviewed 2026-10-07.
   18) Header drop-down menus: the theme prints aria-expanded on each menu's <summary>. A <summary> already
       reports expanded/collapsed from its parent <details>, and aria-expanded is not valid on it, so the
       attribute is removed and kept off. Supersedes the attribute sync in item 8; Escape handling is unchanged.
   19) Search field: the theme marks the search input as an ARIA combobox, but its suggestions are reached with
       Tab rather than arrow keys, and that role is not valid on input type="search". The combobox attributes
       are removed and kept off. The suggestion count is still announced by item 9.
   20) "You may also like" and "Pairs well with": the theme's invisible layout placeholder is made inert
       instead of aria-hidden, so its links can never take keyboard focus (WCAG 4.1.2).
   21) The product form's fallback variant menu gets an accessible name (WCAG 4.1.2).
   22) Share links: the accessible name now contains the visible label, "Share on X" and "Pin it on
       Pinterest" (WCAG 2.5.3).
   23) Breadcrumb navigation: redundant role attribute removed.
   24) Button-style links with generic text such as "Learn More" are named with the heading of the section
       they sit in (WCAG 2.4.4).
   25) Headings left empty by the content editor are taken out of the heading outline until they have text.
   26) Wishlist app hearts on product cards and cart items are injected inside the product link, which puts
       a button inside a link. They are moved next to the link, in the same position (WCAG 4.1.2).
   27) Restock-alert app buttons ("Notify Me") drawn in near-white on olive, about 2:1, get the dark text
       used on the cart drawer button (WCAG 1.4.3).
   28) "Pairs well with" slider: slides that are off screen were aria-hidden but their links could still take
       keyboard focus. Off-screen slides are made inert instead (WCAG 4.1.2).
   29) The "Pairs well with" title is announced at heading level 2 instead of 4 (best practice).
   30) Images inside a link whose alt text only repeats the link's text are marked decorative, including
       images that load late (best practice; extends item 15).
   31) An image link that has no text and no alt text is named with the heading of the section it sits in
       (WCAG 2.4.4, 4.1.2). Found once the wishlist heart was moved out of the homepage feature image link.
   32) On a page with no level-one heading, the first heading in the main content is announced at level 1
       (best practice).
   Added later on 2026-10-07 (round 4b, after a keyboard pass on the published theme):
   33) Product thumbnails: the theme's slideshow code calls blur() on every thumbnail each time the main photo
       changes, so a thumbnail reached with Tab lost focus about 25 ms later and focus fell back to the page.
       A thumbnail that has keyboard focus now ignores that call (WCAG 2.4.3, 2.4.7). Mouse clicks are unchanged.
   34) Carousels that take keyboard focus (product photos, "Pairs well with") get a group role and a name.
   Added 2026-10-13 (round 4c, after checking menus, filters and dialogs in their open states):
   35) Mobile menu: the theme puts role="navigation" on the menu's <ul>, which removes its list semantics and
       leaves the menu items outside any list. The role moves to the list's container, named "Menu" because
       the header navigation already uses the list's old name (WCAG 1.3.1).
   36) Collection filters: the "Flower color" and "Foliage Color" groups reuse the same checkbox ids, so the
       second group's checkboxes had no label and its labels toggled the first group's boxes. Repeated ids
       are made unique and each label is pointed at its own checkbox (WCAG 1.3.1, 4.1.2).
   37) Dialogs added to the page after item 10 has run (Quick view on "You may also like" products) get the
       same dialog role, name and focus return that item 10 gives the others (WCAG 4.1.2, 2.4.3).
   38) Email, name and phone fields in the theme's own newsletter and contact forms get autocomplete tokens
       (WCAG 1.3.5).
   The companion stylesheet a11y-round4.css restores a visible keyboard focus outline on product thumbnails
   (WCAG 2.4.7) and shows the current page number in pagination at full contrast (WCAG 1.4.3).
*/
(function () {
  'use strict';

  var MO = window.MutationObserver;

  function each(selector, fn, root) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }
  function text(el) {
    return el ? el.textContent.replace(/[\s\xa0]+/g, ' ').trim() : '';
  }

  /* Remove the named attributes from an element, and remove them again if another script puts them back */
  function keepOff(el, names) {
    function strip() {
      for (var i = 0; i < names.length; i++) {
        if (el.hasAttribute(names[i])) el.removeAttribute(names[i]);
      }
    }
    strip();
    if (MO && !el.a11yKeepOff) {
      el.a11yKeepOff = true;
      new MO(strip).observe(el, { attributes: true, attributeFilter: names });
    }
  }

  /* --- 18) Header drop-down menus --- */
  function fixMenus() {
    each('details.site-nav__details > summary', function (summary) {
      keepOff(summary, ['aria-expanded']);
    });
  }

  /* --- 19) Search field --- */
  function fixSearch() {
    each('predictive-search input[type="search"]', function (input) {
      keepOff(input, ['role', 'aria-expanded', 'aria-owns', 'aria-controls', 'aria-haspopup', 'aria-autocomplete']);
    });
  }

  /* --- 20) Invisible recommendation placeholders --- */
  function fixPlaceholders() {
    each('.product-recommendations-placeholder > [aria-hidden="true"]', function (p) {
      p.removeAttribute('aria-hidden');
      p.setAttribute('inert', '');
    });
  }

  /* --- 21) Fallback variant menu --- */
  function nameVariantMenus() {
    each('select[data-product-select]', function (select) {
      if (!select.hasAttribute('aria-label') && !select.hasAttribute('aria-labelledby')) {
        select.setAttribute('aria-label', 'Product variant');
      }
    });
  }

  /* --- 22) Share links --- */
  function nameShareLinks() {
    each('.social-sharing__link', function (a) {
      var label = text(a.querySelector('.social-sharing__title'));
      var name = (a.getAttribute('aria-label') || text(a.querySelector('.visually-hidden')) || a.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
      if (!label || !name) return;
      if (name.toLowerCase().indexOf(label.toLowerCase()) !== -1) return; /* "Share on Facebook" already contains "Share" */
      var on = name.match(/\bon\s+\S.*$/i); /* "Tweet on X" gives "on X" */
      var full = on ? label + ' ' + on[0] : label + ' (' + name + ')';
      a.setAttribute('aria-label', full);
      a.setAttribute('title', full);
    });
  }

  /* --- 23) Breadcrumbs --- */
  function fixBreadcrumbs() {
    each('nav.breadcrumb[role="navigation"]', function (nav) { nav.removeAttribute('role'); });
  }

  /* --- 24) Generic link text --- */
  var generic = /^(learn more|read more|shop now|click here|see more|view all|more|here)$/i;
  function nameGenericLinks() {
    each('main .shopify-section a.btn[href]', function (a) {
      if (a.hasAttribute('aria-label') || a.hasAttribute('aria-labelledby')) return;
      var label = text(a);
      if (!generic.test(label)) return;
      var section = a.closest('.shopify-section');
      if (!section) return;
      var heading = '';
      each('h1, h2, h3, h4, h5, h6', function (h) {
        if (text(h) && (h.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING)) heading = text(h);
      }, section);
      if (heading) a.setAttribute('aria-label', label + ': ' + heading.slice(0, 80));
    });
  }

  /* --- 25) Empty headings --- */
  function quietEmptyHeadings() {
    each('h1, h2, h3, h4, h5, h6', function (h) {
      var empty = !text(h) && !h.hasAttribute('aria-label') && !h.querySelector('img[alt]:not([alt=""]), [aria-label]');
      if (empty && !h.hasAttribute('aria-hidden') && !h.hasAttribute('role')) {
        h.setAttribute('role', 'presentation');
        h.setAttribute('aria-hidden', 'true');
        h.setAttribute('data-a11y-empty', '');
      } else if (!empty && h.hasAttribute('data-a11y-empty')) {
        h.removeAttribute('role');
        h.removeAttribute('aria-hidden');
        h.removeAttribute('data-a11y-empty');
      }
    });
  }

  /* --- 26) Wishlist hearts inside links --- */
  function moveWishlistButtons() {
    each('a .nm-portal', function (portal) {
      if (!portal.querySelector('.nm-wishlist-on-card-button')) return;
      var link = portal.closest('a');
      var host = link.closest('.grid-product__content') || link.parentElement;
      if (!host || host === portal.parentElement) return;
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(portal);
    });
  }

  /* --- 27) Restock-alert buttons --- */
  function channel(v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
  function contrast(a, b) {
    var la = 0.2126 * channel(a[0]) + 0.7152 * channel(a[1]) + 0.0722 * channel(a[2]);
    var lb = 0.2126 * channel(b[0]) + 0.7152 * channel(b[1]) + 0.0722 * channel(b[2]);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }
  function rgb(s) { var m = String(s).match(/[\d.]+/g); return m && m.length >= 3 ? m.map(Number) : null; }
  function fixNotifyButtons() {
    each('.restock-alerts-notify-button', function (b) {
      var cs = window.getComputedStyle(b);
      var fg = rgb(cs.color);
      var bg = rgb(cs.backgroundColor);
      if (!fg || !bg || bg[3] === 0) return;
      if (contrast(fg, bg) >= 4.5) return;
      if (contrast([34, 34, 34], bg) >= 4.5) b.style.setProperty('color', '#222222', 'important');
    });
  }

  /* --- 28) Recommendation slider --- */
  function syncSlides() {
    each('.flickity-enabled', function (slider) {
      var slides = slider.querySelectorAll('.product-recommendations__slide');
      if (!slides.length || !slider.querySelector('.product-recommendations__slide.is-selected')) return;
      Array.prototype.forEach.call(slides, function (slide) {
        if (slide.classList.contains('is-selected')) {
          if (slide.hasAttribute('inert')) slide.removeAttribute('inert');
        } else {
          if (!slide.hasAttribute('inert')) slide.setAttribute('inert', '');
          if (slide.hasAttribute('aria-hidden')) slide.removeAttribute('aria-hidden');
        }
      });
    });
  }
  function watchSliders() {
    each('.flickity-enabled', function (slider) {
      if (slider.a11ySlides || !slider.querySelector('.product-recommendations__slide')) return;
      slider.a11ySlides = true;
      if (MO) new MO(syncSlides).observe(slider, { attributes: true, subtree: true, attributeFilter: ['class', 'aria-hidden'] });
    });
    syncSlides();
  }

  /* --- 29) "Pairs well with" heading level --- */
  function fixRecommendationHeadings() {
    each('h4.product-recommendations__title', function (h) {
      if (!h.hasAttribute('aria-level')) {
        h.setAttribute('role', 'heading');
        h.setAttribute('aria-level', '2');
      }
    });
  }

  /* --- 30) Images that repeat their link's text --- */
  function linkText(a) {
    var out = '';
    var walker = document.createTreeWalker(a, NodeFilter.SHOW_TEXT, null, false);
    var n;
    while ((n = walker.nextNode())) {
      if (n.parentNode && n.parentNode.closest && n.parentNode.closest('style, script, svg')) continue;
      out += ' ' + n.nodeValue;
    }
    return out.replace(/[\s\xa0]+/g, ' ').trim();
  }
  function quietRedundantAlts() {
    each('a[href] img[alt]', function (img) {
      var alt = img.getAttribute('alt').replace(/[\s\xa0]+/g, ' ').trim().toLowerCase();
      if (alt && linkText(img.closest('a')).toLowerCase() === alt) img.setAttribute('alt', '');
    });
  }

  /* --- 31) Image links without a name --- */
  function nameImageLinks() {
    each('main .shopify-section a[href]', function (a) {
      if (a.hasAttribute('aria-label') || a.hasAttribute('aria-labelledby') || a.hasAttribute('title')) return;
      if (!a.querySelector('img')) return;
      if (linkText(a) || a.querySelector('img[alt]:not([alt=""])')) return;
      var section = a.closest('.shopify-section');
      var before = '';
      var first = '';
      each('h1, h2, h3, h4, h5, h6', function (h) {
        var t = text(h);
        if (!t) return;
        if (!first) first = t;
        if (h.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING) before = t;
      }, section);
      var name = before || first;
      if (name) a.setAttribute('aria-label', name.slice(0, 80));
    });
  }

  /* --- 32) Pages without a level-one heading --- */
  function ensurePageHeading() {
    if (document.querySelector('h1, [role="heading"][aria-level="1"]')) return;
    var done = false;
    each('main h2, main h3', function (h) {
      if (done || !text(h) || h.closest('[aria-hidden="true"], [inert]')) return;
      h.setAttribute('role', 'heading');
      h.setAttribute('aria-level', '1');
      done = true;
    });
  }

  /* --- 33) Product thumbnails keep keyboard focus --- */
  function keepThumbFocus() {
    each('.product__thumb', function (thumb) {
      if (thumb.a11yBlur) return;
      thumb.a11yBlur = true;
      var nativeBlur = thumb.blur;
      thumb.blur = function () {
        var keyboard = false;
        try { keyboard = thumb.matches(':focus-visible'); } catch (e) { keyboard = false; }
        if (keyboard && document.activeElement === thumb) return;
        nativeBlur.call(thumb);
      };
    });
  }

  /* --- 34) Carousels that take keyboard focus --- */
  function nameCarousels() {
    each('.flickity-enabled[tabindex]', function (c) {
      if (c.hasAttribute('role') || c.hasAttribute('aria-label') || c.hasAttribute('aria-labelledby')) return;
      var name = '';
      if (c.classList.contains('product-slideshow')) {
        name = 'Product images';
      } else {
        var host = c.closest('product-recommendations, .shopify-section');
        each('h1, h2, h3, h4, h5, h6', function (h) { if (!name && text(h)) name = text(h); }, host || c);
      }
      if (!name) return;
      c.setAttribute('role', 'group');
      c.setAttribute('aria-label', name.slice(0, 60) + ' carousel');
    });
  }

  /* --- 35) Mobile menu list semantics --- */
  function fixMobileNav() {
    each('ul.mobile-nav[role="navigation"]', function (ul) {
      var parent = ul.parentElement;
      var label = ul.getAttribute('aria-label') || 'Menu';
      ul.removeAttribute('role');
      ul.removeAttribute('aria-label');
      if (!parent || parent.tagName === 'NAV' || parent.hasAttribute('role')) return;
      each('nav, [role="navigation"]', function (other) {
        if (other !== parent && other.getAttribute('aria-label') === label) label = 'Menu';
      });
      parent.setAttribute('role', 'navigation');
      parent.setAttribute('aria-label', label);
    });
  }

  /* --- 36) Filter checkboxes with repeated ids --- */
  function fixFilterIds() {
    var seen = {};
    each('input.tag__input[id]', function (input) {
      var id = input.id;
      if (!Object.prototype.hasOwnProperty.call(seen, id)) { seen[id] = true; return; }
      var n = 2;
      while (document.getElementById(id + '-' + n)) n++;
      var label = input.closest('label');
      input.id = id + '-' + n;
      if (label && label.getAttribute('for') === id) label.setAttribute('for', input.id);
    });
  }

  /* --- 37) Dialogs that item 10 does not cover --- */
  var lastBeforeModal = null;
  function modalOpen() { return !!document.querySelector('.modal.modal--is-active'); }
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || t === document.body || t.nodeType !== 1) return;
    if ((t.closest && t.closest('.modal')) || modalOpen()) return;
    lastBeforeModal = t;
  }, true);
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('button, a, [tabindex]') : null;
    if (!t || t.closest('.modal') || modalOpen()) return;
    lastBeforeModal = t;
  }, true);
  function setupLateModals() {
    each('.modal', function (modal) {
      if (modal.a11yModal) return;
      modal.a11yModal = true;
      var before = null;
      var mine = false; /* true once this script, rather than item 10, looks after the dialog */
      function label(final) {
        if (modal.getAttribute('aria-labelledby') || modal.getAttribute('aria-label')) return;
        var h = modal.querySelector('h1, h2, h3, .h1, .h2, .h3, .product-single__title');
        if (h && text(h)) {
          if (!h.id) h.id = (modal.id || 'modal') + '-title';
          modal.setAttribute('aria-labelledby', h.id);
        } else if (final) {
          modal.setAttribute('aria-label', /quick-shop/.test(modal.className) ? 'Quick shop' : 'Dialog');
        }
      }
      function onOpen() {
        var opener = lastBeforeModal;
        /* Item 10 in a11y-enhancements.js gives each dialog it looks after a role as the dialog opens.
           Let it go first, then take on only a dialog that still has no role. */
        window.setTimeout(function () {
          if (!mine && modal.getAttribute('role')) return;
          mine = true;
          if (!modal.getAttribute('role')) modal.setAttribute('role', 'dialog');
          if (!modal.getAttribute('aria-modal')) modal.setAttribute('aria-modal', 'true');
          before = opener;
          label(false);
          window.setTimeout(function () { label(false); }, 400);
          window.setTimeout(function () { label(true); }, 1500);
        }, 0);
      }
      function onClose() {
        if (!mine) return;
        window.setTimeout(function () {
          var ae = document.activeElement;
          if ((!ae || ae === document.body) && before && document.contains(before)) {
            try { before.focus({ preventScroll: true }); } catch (err) { try { before.focus(); } catch (e2) {} }
          }
          before = null;
        }, 600);
      }
      var active = modal.classList.contains('modal--is-active');
      if (active) onOpen();
      if (MO) {
        new MO(function () {
          var now = modal.classList.contains('modal--is-active');
          if (!active && now) onOpen();
          if (active && !now) onClose();
          active = now;
        }).observe(modal, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  /* --- 38) Autocomplete tokens on the visitor's own contact details --- */
  function addAutocomplete() {
    each('input[name="contact[email]"]:not([autocomplete])', function (i) { i.setAttribute('autocomplete', 'email'); });
    each('input[name="contact[name]"]:not([autocomplete])', function (i) { i.setAttribute('autocomplete', 'name'); });
    each('input[name="contact[phone]"]:not([autocomplete])', function (i) { i.setAttribute('autocomplete', 'tel'); });
  }

  function runAll() {
    fixMenus();
    fixSearch();
    fixPlaceholders();
    nameVariantMenus();
    nameShareLinks();
    fixBreadcrumbs();
    nameGenericLinks();
    quietEmptyHeadings();
    moveWishlistButtons();
    fixNotifyButtons();
    watchSliders();
    fixRecommendationHeadings();
    quietRedundantAlts();
    nameImageLinks();
    ensurePageHeading();
    keepThumbFocus();
    nameCarousels();
    fixMobileNav();
    fixFilterIds();
    setupLateModals();
    addAutocomplete();
  }
  runAll();

  /* Re-apply when content is added later (quick shop, recommendations, search suggestions, app widgets) */
  var timer = null;
  if (MO) {
    new MO(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(runAll, 300);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }
})();
